/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import type { PassImageConfig, ImageSourceConfig } from './types';

/**
 * Configuration for different pass types and their image structures
 */
export const passImageConfig: PassImageConfig = {
    annual: {
        structure: 'year-based',
        sizes: ['xs', 'sm', 'md', 'lg', 'xl', 'xs_sq'],
    },
    'senior-annual': {
        structure: 'year-based',
        sizes: ['xs', 'sm', 'md', 'lg', 'xl', 'xs_sq'],
    },
    'senior-annual-current-previous': {
        structure: 'year-based',
        sizes: ['xs', 'sm', 'md', 'lg', 'xl'],
        customPath: 'senior-annual',
        customFilename: 'senior_annual_pass_current_previous',
    },
    'military-annual': {
        structure: 'year-based',
        sizes: ['xs', 'sm', 'md', 'lg', 'xl', 'xs_sq'],
    },
    'senior-lifetime': {
        structure: 'version-based',
        sizes: ['xs', 'sm', 'md', 'lg', 'xl', 'xs_sq'],
        fallbackVersion: 'v1',
    },
    'senior-lifetime-current-previous': {
        structure: 'version-based',
        sizes: ['xs', 'sm', 'md', 'lg', 'xl'],
        fallbackVersion: 'v1',
        customPath: 'senior-lifetime',
        customFilename: 'senior_lifetime_pass_current_previous',
    },
    'military-lifetime': {
        structure: 'version-based',
        sizes: ['xs', 'sm', 'md', 'lg', 'xl', 'xs_sq'],
        fallbackVersion: 'v1',
    },
    access: {
        structure: 'version-based',
        sizes: ['xs', 'sm', 'md', 'lg', 'xl', 'xs_sq'],
        fallbackVersion: 'v1',
    },
    'access-current-previous': {
        structure: 'version-based',
        sizes: ['xs', 'sm', 'md', 'lg', 'xl'],
        fallbackVersion: 'v1',
        customPath: 'access',
        customFilename: 'access_pass_current_previous',
    },
    'fourth-grade': {
        structure: 'year-based',
        sizes: ['xs', 'sm', 'md', 'lg', 'xl', 'xs_sq'],
    },
    resident: {
        structure: 'year-based',
        sizes: ['xs', 'sm', 'md', 'lg', 'xl', 'xs_sq'],
    },
    nonresident: {
        structure: 'year-based',
        sizes: ['xs', 'sm', 'md', 'lg', 'xl', 'xs_sq'],
    },
    'golden-age': {
        structure: 'version-based',
        sizes: ['xs', 'sm', 'md', 'lg', 'xl'],
        fallbackVersion: 'v1',
        customPath: 'legacy',
    },
    'golden-access': {
        structure: 'version-based',
        sizes: ['xs', 'sm', 'md', 'lg', 'xl'],
        fallbackVersion: 'v1',
        customPath: 'legacy',
    },
    volunteer: {
        structure: 'year-based',
        sizes: ['xs', 'sm', 'md', 'lg', 'xl', 'xs_sq'],
    },
    generic: {
        structure: 'custom',
        customVariants: ['pass_information_back', 'explore_passes_stacked'],
    },
};

/**
 * Get the current image source configuration based on environment
 */
export function getImageSourceConfig(): ImageSourceConfig {
    // Check for S3 configuration via environment variables
    const s3BaseUrl = process.env.ATB_PASS_IMAGES_S3_URL;

    if (s3BaseUrl) {
        return {
            type: 's3',
            baseUrl: s3BaseUrl,
        };
    }

    // Use the standard shared location - this works because:
    // 1. Master vite config copies shared-ui assets to all services during build
    // 2. CI/CD moves all service images to shared/{service}/img/, making them web-accessible
    // 3. Proxy fallback system handles missing assets by routing to target server
    return {
        type: 'local',
        baseUrl: '/shared/img/interagency-passes',
    };
}
